import type { ReactNode } from "react"
import { Link } from "react-router"
import {
  CUSTOMER_REQUESTS_BASE,
  customerRequestDetailPath,
} from "../../../routes"
import { DashboardRow } from "./DashboardRow"

interface TopRowItem {
  questionnaireId: string
  customerId?: string | null
  customerName?: string | null
  reference?: string | null
}

/**
 * Shared row shell behind the Open/Waiting/Gaps widgets' "top example rows"
 * — title (customer-name link, or reference/id fallback) + reference-dedupe
 * subtitle (see Finding 6: a null-customerName row with a reference
 * otherwise showed the same reference twice, once as the title and once in
 * the subtitle) + metric + action. Only the subtitle's trailing content and
 * the metric/metricLabel/action are per-widget, so those stay as props
 * computed by the caller.
 */
export function RequestTopRow({
  item,
  subtitleTail,
  metric,
  metricLabel,
  action,
}: {
  item: TopRowItem
  subtitleTail: ReactNode
  metric: ReactNode
  metricLabel: string
  action: { label: string; to: string }
}) {
  const to = customerRequestDetailPath(item.questionnaireId)
  const titleIsReference = !item.customerName

  return (
    <DashboardRow
      title={
        item.customerName ? (
          <Link
            // Spec drill-down rule: the customer NAME deep-links to the
            // customer-filtered request list, not the record (the row's own
            // action button always goes to the record — see the always-
            // present `action` prop). Falls back to the record route when
            // customerId is absent (e.g. a soft-deleted customer).
            to={
              item.customerId
                ? `${CUSTOMER_REQUESTS_BASE}?customer=${item.customerId}`
                : to
            }
            className="font-semibold text-white underline decoration-grey5 underline-offset-4 hover:decoration-white"
          >
            {item.customerName}
          </Link>
        ) : (
          <span className="font-semibold text-white">
            {item.reference ?? item.questionnaireId}
          </span>
        )
      }
      subtitle={
        <>
          {item.reference && !titleIsReference && (
            <span className="font-mono">{item.reference}</span>
          )}
          {item.reference && !titleIsReference && " · "}
          {subtitleTail}
        </>
      }
      metric={metric}
      metricLabel={metricLabel}
      action={action}
    />
  )
}
